import {
  Injectable,
  inject,
  PLATFORM_ID,
  EnvironmentInjector,
  runInInjectionContext,
  NgZone,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import {
  Auth as FirebaseAuth,
  signInWithEmailAndPassword,
  signOut,
  User as FirebaseUser,
  onAuthStateChanged,
  User,
} from '@angular/fire/auth';
import { BehaviorSubject, filter, shareReplay, take } from 'rxjs';
import { environment } from '@environments/environment';
import { FirebaseLoginUser } from '../models/auth.model';
import { Result } from '@core/models/common.model';

@Injectable({
  providedIn: 'root',
})
export class FirebaseAuthService {
  private readonly fireAuth = inject(FirebaseAuth);
  private readonly injector = inject(EnvironmentInjector);
  private readonly platformId = inject(PLATFORM_ID);

  public currentFbUser = signal<FirebaseUser | null>(null);

  private readonly initializedSubject = new BehaviorSubject<boolean>(false);
  public readonly ready$ = this.initializedSubject.asObservable().pipe(
    filter(Boolean),
    take(1),
    shareReplay(1),
  );

  private readonly currentFbUserSubject = new BehaviorSubject<FirebaseUser | null>(null);
  public readonly currentFbUser$ = this.currentFbUserSubject
    .asObservable()
    .pipe(shareReplay(1));

  private readonly currentTokenSubject = new BehaviorSubject<string | null>(null);
  public readonly currentToken$ = this.currentTokenSubject.asObservable();

  constructor(
    private router: Router,
    private ngZone: NgZone,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      runInInjectionContext(this.injector, () => {
        onAuthStateChanged(this.fireAuth, (user: User | null) => {
          this.ngZone.run(async () => {
            await this.handleAuthStateChange(user);
            if (!this.initializedSubject.value) {
              this.initializedSubject.next(true);
            }
          });
        });
      });
    } else {
      this.initializedSubject.next(true);
    }
  }

  private async handleAuthStateChange(user: FirebaseUser | null): Promise<void> {
    if (environment.consoleFlag) {
      console.log('[FirebaseAuth] state changed', user?.email || 'null');
    }

    if (user) {
      this.setFirebaseUser(user);
      try {
        const tokenResult = await user.getIdTokenResult(true);
        if (tokenResult.token) {
          this.currentTokenSubject.next(tokenResult.token);
        }
      } catch (error) {
        console.error('Error handling auth state change:', error);
        await this.logout();
      }
    } else {
      this.currentFbUser.set(null);
      this.currentFbUserSubject.next(null);
      this.currentTokenSubject.next(null);
    }
  }

  setFirebaseUser(user: User): void {
    this.currentFbUser.set(user);
    this.currentFbUserSubject.next(user);
  }

  async getFreshToken(): Promise<string | null> {
    const fbUser = this.currentFbUserSubject.value;
    if (!fbUser) return null;

    try {
      return await fbUser.getIdToken(true);
    } catch (err) {
      console.error('Failed to refresh Firebase token:', err);
      return null;
    }
  }

  async logout(): Promise<void> {
    try {
      await signOut(this.fireAuth);
      this.currentFbUser.set(null);
      this.currentFbUserSubject.next(null);
    } catch (error) {
      console.error('Error during logout:', error);
      throw error;
    }
  }

  async loginWithEmail(
    email: string,
    password: string,
    rememberMe: boolean,
  ): Promise<Result<FirebaseLoginUser>> {
    try {
      const result = await signInWithEmailAndPassword(this.fireAuth, email, password);
      const { uid, email: userEmail, providerData } = result.user;

      const loginUser: FirebaseLoginUser = {
        uid,
        token: await result.user.getIdToken(true),
        provider: providerData[0]?.providerId || 'email',
        registrationMethod: 'email',
        email: userEmail,
        rememberMe,
      };

      // explain rememberMe
      // if rememberMe is true, the user will be logged in for 30 days
      // if rememberMe is false, the user will be logged in for 1 hour
      // if rememberMe is undefined, the user will be logged in for 1 hour
      // if rememberMe is null, the user will be logged in for 1 hour
      // if rememberMe is not provided, the user will be logged in for 1 hour
      // if rememberMe is not provided, the user will be logged in for 1 hour

      return { success: true, data: loginUser };
    } catch (error: unknown) {
      console.error('Email login error:', error);

      if (this.isFirebaseError(error)) {
        return {
          success: false,
          message: this.formatError(error.code),
          errorCode: error.code,
        };
      }

      return {
        success: false,
        message: 'Unexpected login error occurred.',
        errorCode: 'UNEXPECTED_ERROR',
      };
    }
  }

  async getUid(): Promise<string | null> {
    const fbUser = this.currentFbUserSubject.value;
    return fbUser?.uid ?? null;
  }

  private isFirebaseError(error: unknown): error is { code: string; message: string } {
    return !!error && typeof (error as { code?: string }).code === 'string';
  }

  formatError(error: string): string {
    switch (error) {
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/user-not-found':
        return 'No user found with this email.';
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Incorrect email or password.';
      case 'auth/email-already-in-use':
        return 'This email is already registered.';
      case 'auth/weak-password':
        return 'Password is too weak. Use at least 6 characters.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      default:
        return 'An unknown error occurred. Please try again.';
    }
  }
}
